import {
  COMETCHAT_PERMISSIONS,
  getCometchatGroups,
  getCometchatSites,
  getSiteGroup,
  getTenantGroup,
} from '../../services/cometchatApi';
import userService from '../../services/userService';
import { getAllSites } from './siteApi';

const SITE_ONBOARDING_PERMISSION = 'onboarding.site.create';

export const loadCometchatPermissions = async () => {
  const [permissions, user] = await Promise.all([
    userService.getUserPermissions(),
    userService.getCurrentUser(),
  ]);

  const role = (
    user?.spec_role ||
    user?.role_name ||
    user?.role ||
    ''
  ).toLowerCase();

  const canManageChatAdmin =
    permissions.includes(COMETCHAT_PERMISSIONS.TENANT_SETTINGS_MANAGE) ||
    permissions.includes(SITE_ONBOARDING_PERMISSION) ||
    ['tech_admin', 'admin'].includes(role);

  return { canManageChatAdmin };
};

export const formatCometchatApiError = (error, { action } = {}) => {
  if (error?.status === 403) {
    if (action === 'create') {
      return 'You do not have permission to manage chat groups. Ask a tech admin to grant chat.tenant_settings.manage to your role.';
    }
    return 'You do not have permission for this chat admin action.';
  }

  if (error?.status === 500) {
    const message = error?.message || '';
    if (
      message.includes('ValidationException') ||
      message.includes('update expression')
    ) {
      return 'The server failed while saving chat group data (DynamoDB update error). Share the site ID and error with the API team.';
    }
    if (action === 'create') {
      return 'The server failed while creating the chat group. Please retry; if it persists, contact support.';
    }
  }

  return error?.message || 'Something went wrong. Please try again.';
};

export const normalizeCometchatGroupsList = data => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.groups)) return data.groups;
  return [];
};

export const normalizeCometchatSitesList = data => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.sites)) return data.sites;
  if (Array.isArray(data?.items)) return data.items;
  return [];
};

const enrichSitesWithGroupMeta = async sites =>
  Promise.all(
    sites.map(async site => {
      try {
        const group = await getSiteGroup(site.site_id);
        return {
          ...site,
          group_guid: group?.group_guid ?? null,
          shift_sync_active: group?.shift_sync_active,
          cometchat: group?.cometchat ?? null,
        };
      } catch {
        return site;
      }
    })
  );

export const fetchSiteListWithGroupMeta = async () => {
  try {
    const data = await getCometchatSites();
    return normalizeCometchatSitesList(data);
  } catch {
    const sites = await getAllSites();
    const list = Array.isArray(sites) ? sites : [];
    return enrichSitesWithGroupMeta(list);
  }
};

export const fetchGroupListWithMeta = async ({ hasGroupOnly = false } = {}) => {
  try {
    const data = await getCometchatGroups({ hasGroupOnly });
    return normalizeCometchatGroupsList(data);
  } catch {
    const sites = await fetchSiteListWithGroupMeta();
    const rows = sites.map(site => mapSiteToGroupRow(site));

    try {
      const tenantGroup = await getTenantGroup();
      const orgRow = mapGroupToRow({
        level: 'organization',
        site_id: null,
        site_name: 'Organization',
        group_name: tenantGroup?.group_name || 'Organization',
        group_guid: tenantGroup?.group_guid,
        shift_sync_active: tenantGroup?.shift_sync_active,
        cometchat: tenantGroup?.cometchat,
      });
      rows.unshift(orgRow);
    } catch {
      // no org group metadata
    }

    return hasGroupOnly ? rows.filter(row => row.group_guid) : rows;
  }
};

export const getSiteShiftEnabled = site =>
  Boolean(
    site?.shift_sync_active ??
      site?.cometchat?.shift_membership_enabled ??
      site?.shift_membership_enabled
  );

export const formatRelativeSyncTime = value => {
  if (!value) return '—';

  try {
    const date = new Date(value);
    const diffMs = Date.now() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);

    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin} min ago`;

    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr} hr ago`;

    return date.toLocaleString();
  } catch {
    return value;
  }
};

export const mapGroupToRow = group => ({
  id: group.level === 'organization' ? 'organization' : group.site_id,
  site_id: group.site_id || null,
  site_name:
    group.site_name ||
    (group.level === 'organization' ? 'Organization' : 'Unnamed site'),
  group_name:
    group.group_name ||
    group.site_name ||
    (group.level === 'organization' ? 'Organization' : 'Unnamed site'),
  level: group.level || 'site',
  group_guid: group.group_guid ?? group.cometchat?.group_guid ?? null,
  shift_sync_active: Boolean(group.shift_sync_active ?? getSiteShiftEnabled(group)),
  last_synced_at: formatRelativeSyncTime(group.cometchat?.last_synced_at),
  last_sync_status: group.cometchat?.last_sync_status,
});

export const mapSiteToGroupRow = (site, { displayName } = {}) =>
  mapGroupToRow({
    level: 'site',
    site_id: site.site_id,
    site_name: site.site_name,
    group_name: displayName || site.site_name,
    group_guid: site.group_guid,
    shift_sync_active: site.shift_sync_active,
    cometchat: site.cometchat,
  });

export const getTenantShiftEnabled = settings =>
  Boolean(
    settings?.tenant_cometchat?.shift_membership_enabled ??
      settings?.cometchat?.shift_membership_enabled ??
      settings?.shift_membership_enabled
  );

export const groupHasChat = group =>
  Boolean(group?.group_guid || group?.cometchat?.group_guid);

export const siteHasGroup = site => groupHasChat(site);
